/**
 * Deployed identifiers. None of these are secrets: both app clients are public
 * (`generateSecret: false`), which is exactly why PKCE is what protects the
 * flow rather than a shared secret (D47).
 *
 * Committed rather than kept in `.env` so a build is reproducible and a wrong
 * value is a diff rather than a mystery.
 */
/**
 * `adminClientId` used to sit here. It is gone because the admin password
 * screen it served is gone: being an organiser is group membership on the
 * ordinary guest session (D48), never a separate login. The Cognito client
 * still exists and the API's authorizer still accepts its audience —
 * see the note in the todo about retiring it from `WeddingAuth`.
 */
/**
 * The API base, and the one place it may be pointed somewhere else.
 *
 * `VITE_API_BASE` exists so the app can be run against
 * `scripts/mock-api.mjs` — a local stand-in holding a few hundred generated
 * photographs — which is how the gallery gets measured at wedding scale without
 * writing three hundred fake rows into the real table.
 *
 * `scripts/dev.mjs` sets it, and **sets it by default**: `npm run dev` is the
 * mock and `npm run dev:prod` is the deployed stack. It used to be the other
 * way round by omission — the variable had to be remembered, so forgetting it
 * pointed a development build at production.
 *
 * **`import.meta.env.DEV` is the whole safety property.** Vite replaces it with
 * a literal `false` in a production build, so this branch is removed by dead
 * code elimination and a production bundle cannot be pointed anywhere by an
 * environment variable, whatever is set when it is built.
 */
const PROD_API = 'https://sk63dr3e2h.execute-api.ap-southeast-1.amazonaws.com';

const API = (import.meta.env.DEV && import.meta.env.VITE_API_BASE) || PROD_API;

/**
 * Which backend this build is talking to, for the dev-only badge (D58).
 *
 * Derived from the **resolved base** rather than read from `VITE_API_TARGET`,
 * so it cannot disagree with where requests actually go — a badge that says
 * MOCK while the app writes to production is worse than no badge at all.
 * `scripts/dev.mjs` sets that variable too, but only as a hint; this is the
 * authority.
 *
 * Split out as a pure function because this formula **is** the safety property
 * the badge rests on, and mocking the module — which the component's own test
 * does — means the formula itself never runs. `auth-config.test.ts` exercises
 * it directly across both flags.
 *
 * `DEV` is a literal `false` in a production build, so this answers `'prod'`
 * there whatever the base is, and the badge reading it is eliminated anyway by
 * its own guard.
 */
export function targetFor(api: string, isDev: boolean): 'mock' | 'prod' {
  return isDev && api !== PROD_API ? 'mock' : 'prod';
}

export const API_TARGET: 'mock' | 'prod' = targetFor(API, import.meta.env.DEV);

export const AUTH = {
  region: 'ap-southeast-1',
  userPoolId: 'ap-southeast-1_R1i2zKpRD',
  guestClientId: '6stlimbe964pvj2cfng8l58f9e',
  /** Managed login. Served by a CloudFront distribution Cognito owns. */
  domain: 'https://auth.megandvideet.com',
  api: API,
} as const;

/**
 * Cognito's native passwordless EMAIL_OTP sends **eight** digits, not six, and
 * the length is not configurable. The app was built for six — every screen, the
 * input grid and the email copy all said so — which would have rejected every
 * real code while looking entirely correct.
 */
export const CODE_LENGTH = 8;

export const redirectUri = () => `${window.location.origin}/auth/callback`;
export const logoutUri = () => `${window.location.origin}/`;
